import { inject, Injectable } from '@angular/core';
import { STORAGE_KEYS } from '../constants/storage-keys.const';
import { UserStateService } from './state-service/user-state.service';
import { StorageService } from './storage.service';

type RememberedDatasets = Record<string, string>;

/**
 * Remembers which dataset each user last opened, so the same account lands in
 * the same place on the next login. Callers must still validate a read against
 * the current dataset list — see `resolveDefaultDataset`.
 */
@Injectable({ providedIn: 'root' })
export class DatasetPreferenceService {
  private readonly storageService = inject(StorageService);
  private readonly userStateService = inject(UserStateService);

  getRemembered(): string | null {
    const userKey = this.userStateService.currentUserKey;
    if (!userKey) {
      return null;
    }

    return this.read()[userKey] ?? null;
  }

  remember(datasetName: string): void {
    const userKey = this.userStateService.currentUserKey;
    if (!userKey) {
      return;
    }

    this.storageService.setLocal(STORAGE_KEYS.SELECTED_DATASET, {
      ...this.read(),
      [userKey]: datasetName
    });
  }

  private read(): RememberedDatasets {
    const stored = this.storageService.getLocal<unknown>(
      STORAGE_KEYS.SELECTED_DATASET
    );

    if (
      typeof stored !== 'object' ||
      stored === null ||
      Array.isArray(stored)
    ) {
      return {};
    }

    return stored as RememberedDatasets;
  }
}
